import axios from "axios";

import CONFIG from "../utils/config";
import * as ApsModule from "./aps";

const AxiosInstanceLegacy = axios.create({
  baseURL: `${CONFIG.API_URL}/v2/legacy`,
});

const AxiosInstance = axios.create({
  baseURL: `${CONFIG.API_URL}/v2`,
});

export const HttpsInstanceAuth = axios.create({
  baseURL: `https://${CONFIG.AUTH0_DOMAIN}/dbconnections`,
});

export const HttpInstanceTransfers = axios.create({
  baseURL: `${CONFIG.TRANSFERS_API_URL}`,
});

export const HttpInstanceEPSG = axios.create({
  baseURL: "https://epsg.io",
});

AxiosInstance.interceptors.request.use(async (config) => {
  if (config && config.headers) {
    config.headers["Content-Type"] = "application/json";
  }
  if (!window.getAccessToken) {
    return config;
  }
  const token = await window.getAccessToken();

  if (config && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

AxiosInstanceLegacy.interceptors.request.use(async (config) => {
  if (config && config.headers) {
    config.headers["Content-Type"] = "application/json";
  }
  if (!window.getAccessToken) {
    return config;
  }
  const token = await window.getAccessToken();

  if (config && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

HttpsInstanceAuth.interceptors.request.use(async (config) => {
  if (config && config.headers) {
    config.headers["Content-Type"] = "application/json";
  }
  if (!window.getAccessToken) {
    return config;
  }
  const token = await window.getAccessToken();

  if (config && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

HttpInstanceTransfers.interceptors.request.use(async (config) => {
  const token = await window.getAccessToken();

  if (config && config.headers) {
    config.headers["Content-Type"] = "application/json";
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

const User = {
  getProfile: () => AxiosInstanceLegacy.get<Profile>("/profile"),
  updateProfile: (data: UpdateProfile) =>
    AxiosInstanceLegacy.patch("/updateProfile", data),
  getMetadata: () => AxiosInstanceLegacy.get<UserMetadata>("/metadata"),
  updateMetadata: (data: UserMetadata) =>
    AxiosInstanceLegacy.patch("/metadata", data),
  changePassword: (data: UpdatePassword) =>
    HttpsInstanceAuth.post("/change_password", data),
  onboard: (data: OnboardPayload) =>
    AxiosInstanceLegacy.post<Workspace>("/user/onboard", data),
  resendVerification: () =>
    AxiosInstanceLegacy.post("/resendVerification", {
      clientId: CONFIG.AUTH0_CLIENT_ID,
    }),
  linkAccount: () => AxiosInstanceLegacy.post<LinkAccount>("/link"),
};

const Workspace = {
  createWorkspace: (data: CreateWorkspacePayload) =>
    AxiosInstanceLegacy.post<Workspace>("/workspaces", data),
  updateWorkspace: (workspaceId: string, data: UpdateWorkspacePayload) =>
    AxiosInstanceLegacy.put<Workspace>(`/workspaces/${workspaceId}`, data),
  deleteWorkspace: (workspaceId: string) =>
    AxiosInstanceLegacy.delete(`/workspaces/${workspaceId}`),
  purchaseCredits: (workspaceId: string, credits: number) =>
    AxiosInstance.post(`/workspace/${workspaceId}/credits`, {
      credits,
    }),
};

const WorkspaceUser = {
  getWorkspaceUsers: (workspaceId: string) =>
    AxiosInstanceLegacy.get<WorkspaceUser[]>(
      `/workspaces/${workspaceId}/users`
    ),
  createWorkspaceUser: (
    workspaceId: string,
    data: CreateWorkspaceUserPayload
  ) =>
    AxiosInstanceLegacy.post<WorkspaceUser>(
      `/workspaces/${workspaceId}/users`,
      data
    ),
  updateWorkspaceUser: (
    workspaceId: string,
    workspaceUserId: string,
    data: UpdateWorkspaceUserPayload
  ) =>
    AxiosInstanceLegacy.put<WorkspaceUser>(
      `/workspaces/${workspaceId}/users/${workspaceUserId}`,
      data
    ),
  deleteWorkspaceUser: (workspaceId: string, workspaceUserId: string) =>
    AxiosInstanceLegacy.delete(
      `/workspaces/${workspaceId}/users/${workspaceUserId}`
    ),
};

const Notification = {
  getNotifications: (page: number, perPage: number) =>
    AxiosInstanceLegacy.get<PaginatedResponse<AppNotification>>(
      `/notifications?page=${page}&perPage=${perPage}`
    ),
  updateNotification: (id: string) =>
    AxiosInstanceLegacy.put<AppNotification>(`/notifications/${id}`),
  bulkUpdateNotifications: (ids: string[]) =>
    AxiosInstanceLegacy.put<AppNotification[]>("/notifications", { ids }),
};

const Project = {
  getProject: (workspaceId: string, projectId: string) =>
    AxiosInstanceLegacy.get<Project>(
      `/workspaces/${workspaceId}/projects/${projectId}`
    ),
  deleteProject: (workspaceId: string, projectId: string) =>
    AxiosInstanceLegacy.delete(
      `/workspaces/${workspaceId}/projects/${projectId}`
    ),
  readProjectPods: (workspaceId: string, projectId: string) =>
    AxiosInstanceLegacy.get(
      `/workspaces/${workspaceId}/projects/${projectId}/pods`
    ),
};

const Scan = {
  // No get scans since we're getting those from the project. We can implement pagination later
  getScan: (scanId: string) =>
    AxiosInstanceLegacy.get<Project>(`/scans/${scanId}`),
  createScan: (data: CreateScanPayload) =>
    AxiosInstanceLegacy.post<Project>(`/scans`, data),
  deleteScan: (scanId: string) =>
    AxiosInstanceLegacy.delete(`/scans/${scanId}`),
  getKeypoints: (scanId: string) =>
    AxiosInstance.get(`/scan/${scanId}/density-floorplan-keypoints`),
  saveKeypoints: (scanId: string, data: any) =>
    AxiosInstance.patch(`/scan/${scanId}/density-floorplan-keypoints`, data),
};

const Input = {
  createInput: (
    workspaceId: string,
    projectId: string,
    data: CreateInputPayload
  ) =>
    AxiosInstanceLegacy.post(
      `/workspaces/${workspaceId}/projects/${projectId}/inputs`,
      data
    ),
  downloadQualityReport: (
    workspaceId: string,
    projectId: string,
    inputId: string
  ) =>
    AxiosInstanceLegacy.get(
      `/workspaces/${workspaceId}/projects/${projectId}/inputs/${inputId}/qualityReport`
    ),
  triggerProcessing: (
    workspaceId: string,
    projectId: string,
    inputId: string,
    data: TriggerProcessingPayload
  ) =>
    AxiosInstanceLegacy.put(
      `/workspaces/${workspaceId}/projects/${projectId}/inputs/${inputId}`,
      data
    ),
  reTriggerProcessing: (
    workspaceId: string,
    projectId: string,
    inputId: string,
    stage: string
  ) =>
    AxiosInstanceLegacy.post(
      `/workspaces/${workspaceId}/projects/${projectId}/inputs/${inputId}/retrigger`,
      { stage }
    ),
};

const Output = {
  downloadOutput: (outputId: string) =>
    AxiosInstanceLegacy.get<DownloadResponse>(`/output/${outputId}/download`),
  downloadOutputPart: (
    workspaceId: string,
    projectId: string,
    outputId: string,
    inputId: string,
    partId: string
  ) =>
    AxiosInstanceLegacy.get<SingleDownloadResponse>(
      `/workspaces/${workspaceId}/projects/${projectId}/inputs/${inputId}/outputs/${outputId}/parts/${partId}/download`
    ),
  createMoreOutputs: (
    workspaceId: string,
    projectId: string,
    inputId: string,
    data: CreateMoreOutputsPayload
  ) =>
    AxiosInstanceLegacy.post(
      `/workspaces/${workspaceId}/projects/${projectId}/inputs/${inputId}/outputs`,
      data
    ),
};

const Transaction = {
  getTransactions: (workspaceId: string, page: number, perPage: number) =>
    AxiosInstanceLegacy.get<PaginatedResponse<Transaction>>(
      `/workspaces/${workspaceId}/transactions?page=${page}&perPage=${perPage}`
    ),
  createTransaction: (credits: number, workspaceId: string) =>
    AxiosInstanceLegacy.post(`/workspaces/${workspaceId}/transactions`, {
      credits,
    }),
};

const File = {
  getInputFiles: (
    workspaceId: string,
    projectId: string,
    inputId: string,
    page: number,
    perPage: number,
    query?: string
  ) =>
    AxiosInstanceLegacy.get<PaginatedResponse<FileDetails>>(
      `/workspaces/${workspaceId}/projects/${projectId}/inputs/${inputId}/files?page=${page}&perPage=${perPage}${
        query ? "&query=" + query : ""
      }`
    ),
  getInputFile: (
    workspaceId: string,
    projectId: string,
    inputId: string,
    fileId: string
  ) =>
    AxiosInstanceLegacy.get<FileDetails>(
      `/workspaces/${workspaceId}/projects/${projectId}/inputs/${inputId}/files/${fileId}`
    ),
};

const Transfer = {
  validate: (url: string, inputType: InputType) =>
    HttpInstanceTransfers.post("/validate", { url, inputType }),
  download: (
    workspaceId: string,
    projectId: string,
    url: string,
    inputType: InputType,
    outputs: { type: OutputType; metadata: OutputMetadata }[]
  ) =>
    HttpInstanceTransfers.post("/download", {
      workspaceId,
      projectId,
      url,
      inputType,
      outputs,
    }),
};

const Staff = {
  checkStaffAccess: () => AxiosInstanceLegacy.get("/staff"),
  readWorkspaces: () =>
    AxiosInstanceLegacy.get<StaffWorkspace[]>("/staff/workspaces"),
  getFeatureFlags: () =>
    AxiosInstanceLegacy.get<Array<FeatureFlag>>("/staff/featureFlags"),
  updateWorkspaceFeatureFlags: (
    workspaceId: string,
    featureFlags: Array<WorkspaceFeatureFlag>
  ) =>
    AxiosInstanceLegacy.put<StaffWorkspace>(
      `/staff/workspaces/${workspaceId}/featureFlags`,
      { featureFlags }
    ),
  addUser: (email: string, password: string, name: string, overwrite: boolean) =>
    AxiosInstance.post("/staff/add-custom-user", {
      email,
      password,
      name,
      overwrite,
    }),
  addCredits: (
    workspaceId: string,
    creditsToBeAdded: number,
    amount: number,
    stripeTransactionId: string
  ) =>
    AxiosInstanceLegacy.post<Workspace>("/staff/addCredits", {
      workspaceId,
      creditsToBeAdded,
      amount,
      stripeTransactionId,
    }),
  updateMinimumSupportedMobileVersion: (version: string) =>
    AxiosInstance.put<{ minimumSupportedMobileVersion: string }>(
      "/staff/minimum-supported-mobile-version",
      { minimumSupportedMobileVersion: version }
    ),
};

const App = {
  checkVersion: (version: string) =>
    AxiosInstance.get<{ supported: boolean; minimumSupportedVersion: string }>(
      `/app/version/${version}`
    ),
  getMinimumSupportedVersion: () =>
    AxiosInstance.get<{ minimumSupportedVersion: string }>(
      "/app/version"
    ),
};

const Search = {
  search: (searchQuery: string) =>
    AxiosInstanceLegacy.get(`/search?query=${searchQuery}`),
};

const EPSG = {
  search: (searchQuery: string) =>
    HttpInstanceEPSG.get<CRSSearchResponse>(`/?q=${searchQuery}&format=json`),
};

const API = {
  User,
  Workspace,
  WorkspaceUser,
  Project,
  Scan,
  Input,
  Output,
  Notification,
  Transaction,
  File,
  Transfer,
  Staff,
  Search,
  EPSG,
  App,
  Aps: {
    getHubs: (accessToken: string) =>
      AxiosInstance.post('/aps/get-hubs', { accessToken }),
  },
};

export default API;
